// LOS NÚMEROS DE LAS GRÁFICAS DE PÓLIZAS DE LOS PANELES.
//
// Lissette, 2026-10-12: *"que en paneles de reportes podamos crear un
// dashboard increíble, interactivo, donde a cada gráfica le podamos colocar
// filtro de fecha o personas o estados, dependiendo de cómo se arme ese
// dashboard; fijo, que se actualice automáticamente"*.
//
// Una gráfica de pólizas es una RECETA: qué se mide (pólizas, prima, comisión,
// clientes, miembros), por qué se agrupa (agente, analista, estado, compañía,
// tipo, estatus, mes) y con qué filtros. Los números salen de las MISMAS
// pólizas que la app ya tiene cargadas, y la comisión de la MISMA cuenta que
// Comisiones por Póliza y el Reporte de Ventas (comisionDePoliza.js): el panel
// no puede decir una cifra distinta de esas pantallas. Y como son las pólizas
// de la app, cuando alguien guarda una venta el panel cambia solo.
//
// Vive suelto, sin React: son números y se prueban por lo que devuelven.
import { cuentaDeComision } from './comisionDePoliza.js';
import { personasAseguradas, numero } from './comisionPorPersona.js';
import { getPolicyType } from './policyTypes.js';
import { getPolicyStatusMeta } from './policyStatuses.js';
import { contarEtiquetas, pasaElFiltroDeEtiqueta, SIN_ETIQUETA } from './filtroDeEtiquetas.js';

export const MEDIDAS = ['policies', 'premium', 'annualPremium', 'commission', 'clients', 'members'];
export const DIMENSIONES = ['none', 'agent', 'analyst', 'state', 'carrier', 'type', 'status', 'month'];
export const CAMPOS_DE_FECHA = ['saleDate', 'effectiveDate', 'renewalDate'];
export const FILTROS = ['agent', 'analyst', 'state', 'type', 'carrier', 'status', 'clientTag', 'policyTag'];
/**
 * Filtros por ETIQUETA (Lissette 2026-10-22): de la póliza y del cliente de la
 * póliza. No son una dimensión —una póliza lleva varias etiquetas, así que no
 * cabe en un grupo—, solo filtran: pasa si lleva CUALQUIERA de las elegidas
 * (o ninguna, con "Sin etiqueta"). Misma comparación que el filtro de las
 * listas y del Reporte de Ventas (filtroDeEtiquetas.js).
 */
export const FILTROS_DE_ETIQUETA = new Set(['clientTag', 'policyTag']);
const pasaEtiquetas = (k, p, cliente, elegidas) => {
  const ficha = k === 'policyTag' ? p : (cliente || { tags: [] });
  return elegidas.some((v) => pasaElFiltroDeEtiqueta(ficha, v));
};
export const TIPOS_DE_GRAFICA = ['kpi', 'bar', 'line', 'donut', 'table'];
export const PRESETS = ['all', 'thisMonth', 'lastMonth', 'last30', 'last90', 'thisQuarter', 'thisYear', 'lastYear', 'custom'];

/** Las medidas que son DINERO (se pintan con $). */
export const MEDIDAS_DE_DINERO = new Set(['premium', 'annualPremium', 'commission']);

/**
 * Las que NO se reparten en una dona: un mismo cliente con pólizas de dos
 * agentes cuenta una vez en cada uno, así que los pedazos sumarían más que el
 * total. Igual que "personas distintas" en Miembros activos.
 */
export const MEDIDAS_QUE_NO_SUMAN = new Set(['clients']);

// ── Fechas ────────────────────────────────────────────────────────────────
// Todo en texto "YYYY-MM-DD". Las cuentas de días se hacen sobre una fecha a
// MEDIODÍA UTC, así que toISOString() devuelve el mismo día que se armó (la
// trampa de CLAUDE.md es pasar una fecha LOCAL por toISOString, no esto).
const ISO = /^\d{4}-\d{2}-\d{2}$/;
const dos = (n) => String(n).padStart(2, '0');

/** Hoy en la zona del navegador, "YYYY-MM-DD". */
export const hoyLocal = (d = new Date()) => `${d.getFullYear()}-${dos(d.getMonth() + 1)}-${dos(d.getDate())}`;

export function sumarDias(ymd, n) {
  const d = new Date(`${ymd}T12:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

const diasEntre = (a, b) => Math.round((Date.parse(`${b}T12:00:00Z`) - Date.parse(`${a}T12:00:00Z`)) / 86400000);

/**
 * { from, to } de un preset, contado desde `hoy`. '' = sin tope.
 *
 * La misma cuenta vive en el servidor (utils/dashboardSchedule.ts,
 * ventanaDelPanel) para el correo programado; una prueba compara las dos.
 */
export function rangoDePreset(preset, from, to, hoy = hoyLocal()) {
  const [ys, ms] = hoy.split('-');
  const y = Number(ys);
  const m = Number(ms);
  switch (preset) {
    case 'thisMonth': return { from: `${ys}-${ms}-01`, to: hoy };
    case 'lastMonth': {
      const fin = sumarDias(`${ys}-${ms}-01`, -1);
      return { from: `${fin.slice(0, 7)}-01`, to: fin };
    }
    case 'last30': return { from: sumarDias(hoy, -30), to: hoy };
    case 'last90': return { from: sumarDias(hoy, -90), to: hoy };
    case 'thisQuarter': return { from: `${ys}-${dos(Math.floor((m - 1) / 3) * 3 + 1)}-01`, to: hoy };
    case 'thisYear': return { from: `${ys}-01-01`, to: hoy };
    case 'lastYear': return { from: `${y - 1}-01-01`, to: `${y - 1}-12-31` };
    case 'custom': return { from: ISO.test(from || '') ? from : '', to: ISO.test(to || '') ? to : '' };
    default: return { from: '', to: '' };
  }
}

/**
 * El período de antes, del mismo largo, para la comparación de un indicador.
 * null si el rango no tiene principio y fin (no hay "antes" de "todo").
 */
export function rangoAnterior(r) {
  if (!r?.from || !r?.to) return null;
  const largo = diasEntre(r.from, r.to);
  const to = sumarDias(r.from, -1);
  return { from: sumarDias(to, -largo), to };
}

// ── De qué grupo es cada póliza ──────────────────────────────────────────
const MESES_ES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
const MESES_EN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function etiquetaDeMes(clave, lang) {
  const m = /^(\d{4})-(\d{2})$/.exec(clave || '');
  if (!m) return clave;
  return `${(lang === 'es' ? MESES_ES : MESES_EN)[Number(m[2]) - 1]} ${m[1]}`;
}

const limpio = (v) => String(v ?? '').trim();
/** El agente de una póliza: el mismo criterio del Reporte de Ventas. */
export const agenteDe = (p) => limpio(p?.agentLabel || p?.agentName);

/** La clave cruda (para filtrar y agrupar) de una póliza en una dimensión. */
export function claveDe(dim, p, cliente, campoDeFecha = 'saleDate') {
  switch (dim) {
    case 'agent': return agenteDe(p);
    case 'analyst': return limpio(p?.analystName);
    case 'state': return limpio(cliente?.state).toUpperCase();
    case 'carrier': return limpio(p?.carrier);
    case 'type': return limpio(p?.type);
    case 'status': return limpio(p?.status);
    case 'month': return limpio(p?.[campoDeFecha]).slice(0, 7);
    default: return '';
  }
}

/** Lo que se lee en pantalla para una clave. */
export function etiquetaDe(dim, clave, lang) {
  const es = lang === 'es';
  if (!clave) return es ? '(sin dato)' : '(blank)';
  if (FILTROS_DE_ETIQUETA.has(dim) && clave === SIN_ETIQUETA) return es ? '(sin etiqueta)' : '(untagged)';
  if (dim === 'type') {
    const t = getPolicyType(clave);
    return t?.id === clave ? (es ? t.labelEs || t.label : t.label || t.labelEs) : clave;
  }
  if (dim === 'status') return getPolicyStatusMeta(clave, lang).label;
  if (dim === 'month') return etiquetaDeMes(clave, lang);
  return clave;
}

// ── Filtrar y medir ──────────────────────────────────────────────────────
/**
 * Las pólizas que entran: dentro del rango (por el campo de fecha elegido) y
 * que pasan todos los filtros. Un filtro vacío no filtra.
 *
 * Con un rango puesto, una póliza SIN esa fecha no entra — no hay forma de
 * saber si cae dentro — y se cuenta en `sinFecha` para que la gráfica lo diga.
 */
export function filtrarPolizas(polizas, { clientesPorId, rango, campoDeFecha = 'saleDate', filtros = {} }) {
  const conRango = !!(rango?.from || rango?.to);
  const activos = FILTROS.filter((k) => Array.isArray(filtros[k]) && filtros[k].length);
  const fuera = [];
  let sinFecha = 0;
  for (const p of Array.isArray(polizas) ? polizas : []) {
    if (!p || p.deleted) continue;
    const cliente = clientesPorId?.get?.(p.clientId);
    let pasa = true;
    for (const k of activos) {
      if (FILTROS_DE_ETIQUETA.has(k)) {
        if (!pasaEtiquetas(k, p, cliente, filtros[k])) { pasa = false; break; }
        continue;
      }
      const c = claveDe(k, p, cliente, campoDeFecha);
      if (!filtros[k].includes(c)) { pasa = false; break; }
    }
    if (!pasa) continue;
    if (conRango) {
      const f = limpio(p[campoDeFecha]).slice(0, 10);
      if (!ISO.test(f)) { sinFecha += 1; continue; }
      if (rango.from && f < rango.from) continue;
      if (rango.to && f > rango.to) continue;
    }
    fuera.push(p);
  }
  return { polizas: fuera, sinFecha };
}

const redondear = (n) => Math.round(n * 100) / 100;

/** El número de una medida sobre un grupo de pólizas. */
export function medir(medida, polizas) {
  const ps = Array.isArray(polizas) ? polizas : [];
  switch (medida) {
    case 'premium': return redondear(ps.reduce((s, p) => s + numero(p.monthlyPremium), 0));
    case 'annualPremium': return redondear(ps.reduce((s, p) => s + numero(p.monthlyPremium) * 12, 0));
    case 'commission': return redondear(ps.reduce((s, p) => s + numero(cuentaDeComision(p).total), 0));
    case 'members': return ps.reduce((s, p) => s + personasAseguradas(p), 0);
    case 'clients': return new Set(ps.map((p) => p.clientId).filter(Boolean)).size;
    default: return ps.length;
  }
}

/** Todos los meses "YYYY-MM" de a hasta b, para que la línea no salte huecos. */
function mesesSeguidos(a, b) {
  const fuera = [];
  let [y, m] = a.split('-').map(Number);
  const [yb, mb] = b.split('-').map(Number);
  while ((y < yb || (y === yb && m <= mb)) && fuera.length < 120) {
    fuera.push(`${y}-${dos(m)}`);
    m += 1; if (m > 12) { m = 1; y += 1; }
  }
  return fuera;
}

/** El rango que de verdad usa una gráfica: el suyo, o el del panel si sigue al panel. */
export function rangoDeLaGrafica(widget, rangoDelPanel, hoy = hoyLocal()) {
  const preset = widget?.datePreset || 'panel';
  if (preset === 'panel') return rangoDelPanel || { from: '', to: '' };
  return rangoDePreset(preset, widget?.from, widget?.to, hoy);
}

/** Los filtros de la gráfica con los del panel encima (los dos tienen que cumplirse). */
export function unirFiltros(propios = {}, delPanel = {}) {
  const fuera = {};
  for (const k of FILTROS) {
    const a = Array.isArray(propios?.[k]) ? propios[k] : [];
    const b = Array.isArray(delPanel?.[k]) ? delPanel[k] : [];
    if (a.length && b.length) fuera[k] = a.filter((x) => b.includes(x));
    else if (a.length || b.length) fuera[k] = a.length ? a : b;
    // Si los dos filtran y no coinciden en nada, la gráfica queda vacía, que
    // es la respuesta honesta: no hay pólizas que cumplan las dos cosas.
    if ((a.length && b.length) && fuera[k].length === 0) fuera[k] = ['\u0000__ninguno__'];
  }
  return fuera;
}

/**
 * Todo lo que una gráfica necesita para pintarse.
 *
 *   { total, anterior, cambio, filas: [{ clave, name, value, polizas }], sinFecha, rango }
 *
 * `anterior` / `cambio` (en %) solo cuando hay un período de antes con qué
 * comparar. `cambio` = null si antes era 0 (crecer desde cero no es un %).
 */
export function datosDeLaGrafica(widget, { polizas, clientes, lang, rangoDelPanel, filtrosDelPanel, hoy = hoyLocal() }) {
  const w = widget || {};
  const medida = MEDIDAS.includes(w.measure) ? w.measure : 'policies';
  const dim = DIMENSIONES.includes(w.dimension) ? w.dimension : 'none';
  const campoDeFecha = CAMPOS_DE_FECHA.includes(w.dateField) ? w.dateField : 'saleDate';
  const clientesPorId = new Map((Array.isArray(clientes) ? clientes : []).map((c) => [c.id, c]));
  const rango = rangoDeLaGrafica(w, rangoDelPanel, hoy);
  const filtros = unirFiltros(w.filters, filtrosDelPanel);

  const { polizas: ps, sinFecha } = filtrarPolizas(polizas, { clientesPorId, rango, campoDeFecha, filtros });
  const total = medir(medida, ps);

  let anterior = null;
  let cambio = null;
  const antes = rangoAnterior(rango);
  if (antes) {
    anterior = medir(medida, filtrarPolizas(polizas, { clientesPorId, rango: antes, campoDeFecha, filtros }).polizas);
    cambio = anterior > 0 ? Math.round(((total - anterior) / anterior) * 1000) / 10 : null;
  }

  let filas = [];
  if (dim !== 'none') {
    const grupos = new Map();
    for (const p of ps) {
      const clave = claveDe(dim, p, clientesPorId.get(p.clientId), campoDeFecha);
      if (!grupos.has(clave)) grupos.set(clave, []);
      grupos.get(clave).push(p);
    }
    if (dim === 'month') {
      grupos.delete('');
      const claves = [...grupos.keys()].sort();
      // Con un rango, la línea va de punta a punta del rango aunque al
      // principio no haya ventas; sin rango, del primer mes al último.
      const desde = (rango.from || claves[0] || '').slice(0, 7);
      const hasta = (rango.to || claves[claves.length - 1] || '').slice(0, 7);
      const todos = desde && hasta ? mesesSeguidos(desde, hasta) : claves;
      filas = todos.map((clave) => {
        const g = grupos.get(clave) || [];
        return { clave, name: etiquetaDe(dim, clave, lang), value: medir(medida, g), polizas: g };
      });
    } else {
      filas = [...grupos.entries()]
        .map(([clave, g]) => ({ clave, name: etiquetaDe(dim, clave, lang), value: medir(medida, g), polizas: g }))
        .sort((a, b) => b.value - a.value || String(a.name).localeCompare(String(b.name)));
    }
  }
  return { total, anterior, cambio, filas, sinFecha, rango, polizas: ps, medida, dimension: dim };
}

/**
 * Las opciones de cada filtro, sacadas de las pólizas que hay: [{ value, label }].
 * Ordenadas por nombre; un filtro sin opciones no se ofrece.
 */
export function opcionesDeFiltros(polizas, clientes, lang) {
  const clientesPorId = new Map((Array.isArray(clientes) ? clientes : []).map((c) => [c.id, c]));
  const fuera = {};
  // Etiquetas: las de las pólizas, y las de los clientes que tienen pólizas.
  const conSinEtiqueta = (cuenta) => [
    ...cuenta.opciones.map((o) => ({ value: o.label, label: o.label })),
    ...(cuenta.sinEtiqueta ? [{ value: SIN_ETIQUETA, label: lang === 'es' ? '(sin etiqueta)' : '(untagged)' }] : []),
  ];
  const ps = Array.isArray(polizas) ? polizas.filter(Boolean) : [];
  fuera.policyTag = conSinEtiqueta(contarEtiquetas(ps));
  const idsDeClientes = new Set(ps.map((p) => p.clientId).filter(Boolean));
  fuera.clientTag = conSinEtiqueta(contarEtiquetas([...idsDeClientes].map((id) => clientesPorId.get(id)).filter(Boolean)));
  for (const k of FILTROS) {
    if (FILTROS_DE_ETIQUETA.has(k)) continue;
    const vistos = new Set();
    for (const p of Array.isArray(polizas) ? polizas : []) {
      const c = claveDe(k, p, clientesPorId.get(p?.clientId));
      if (c) vistos.add(c);
    }
    fuera[k] = [...vistos]
      .map((value) => ({ value, label: etiquetaDe(k, value, lang) }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }
  return fuera;
}

/** Cómo se escribe un valor de una medida. */
export function formatoDe(medida, v, { corto = false } = {}) {
  const n = Number(v || 0);
  if (MEDIDAS_DE_DINERO.has(medida)) {
    if (corto && Math.abs(n) >= 1000) {
      return Math.abs(n) >= 1e6 ? `$${(n / 1e6).toFixed(1)}M` : `$${(n / 1000).toFixed(n >= 1e4 ? 0 : 1)}k`;
    }
    return '$' + n.toLocaleString('en-US', { minimumFractionDigits: corto ? 0 : 2, maximumFractionDigits: corto ? 0 : 2 });
  }
  if (corto && Math.abs(n) >= 1000) return `${(n / 1000).toFixed(n >= 1e4 ? 0 : 1)}k`;
  return n.toLocaleString('en-US');
}

/**
 * Una plantilla para empezar: lo que un dueño de agencia mira el lunes.
 * Se le da al botón "Plantilla profesional" del editor.
 */
export function plantillaProfesional(nuevoId) {
  const w = (o) => ({ id: nuevoId(), metric: 'policyChart', dateField: 'saleDate', datePreset: 'panel', filters: {}, ...o });
  return [
    w({ title: '', measure: 'policies', dimension: 'none', chartType: 'kpi', width: 'quarter' }),
    w({ title: '', measure: 'premium', dimension: 'none', chartType: 'kpi', width: 'quarter' }),
    w({ title: '', measure: 'commission', dimension: 'none', chartType: 'kpi', width: 'quarter' }),
    w({ title: '', measure: 'clients', dimension: 'none', chartType: 'kpi', width: 'quarter' }),
    // Por mes EFECTIVO, como el resumen de ventas del servidor ("Mes
    // (efectiva)"): toda póliza tiene fecha efectiva, y muchas viejas no
    // tienen fecha de venta — la línea saldría plana.
    w({ title: '', measure: 'policies', dimension: 'month', chartType: 'line', width: 'twoThirds', datePreset: 'thisYear', dateField: 'effectiveDate' }),
    w({ title: '', measure: 'policies', dimension: 'type', chartType: 'donut', width: 'third' }),
    w({ title: '', measure: 'commission', dimension: 'agent', chartType: 'bar', width: 'half' }),
    w({ title: '', measure: 'policies', dimension: 'analyst', chartType: 'bar', width: 'half' }),
    w({ title: '', measure: 'premium', dimension: 'carrier', chartType: 'bar', width: 'half' }),
    w({ title: '', measure: 'policies', dimension: 'state', chartType: 'table', width: 'half' }),
  ];
}
